import type { FC } from "react";
import { useState } from "react";
import { useFormContext, useForm, useController } from "react-hook-form";
import { Box, Stack } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { useMutation } from "@tanstack/react-query";
import type { InputProps } from "../DynamicInput";
import type { DocumentReference, FileReference, MultipleExternalLinkValue } from "../types";
import { getDocumentReferenceKey } from "../types";
import type { FileOwner } from "../../api/files";
import * as FormDialog from "../../shared/FormDialog";
import { ConfirmDeleteDialog } from "../../shared/ConfirmDeleteDialog";
import { ErrorMessage } from "../inlineForm/ErrorMessage";
import { useDynamicFormState } from "../DynamicFormProvider";
import { isAnswerRequestEmpty } from "./isAnswerRequestEmpty";
import { AddDialogTrigger } from "./AddDialogTrigger";
import { DocumentItem } from "../../shared/DocumentItem";
import { FileField } from "../../shared/FileField";
import type { DocumentSourceFormValues } from "../../shared/DocumentSourceFields";
import { DocumentSourceFields, toDocumentSourceFormValues } from "../../shared/DocumentSourceFields";

export const MultipleExternalLinkInput: FC<InputProps<MultipleExternalLinkValue>> = ({
  questionCard,
  onSave,
  readonly,
}) => {
  const { t } = useTranslation();
  const { mandatoryQuestionErrorMessage, fileOwner } = useDynamicFormState();
  const [dialogState, setDialogState] = useState<
    | { type: "closed" }
    | { type: "edit"; reference: DocumentReference; index: number }
    | { type: "confirmFileDelete"; file: FileReference }
  >({ type: "closed" });

  const { control } = useFormContext();
  const saveMutation = useMutation({
    mutationFn: (value: MultipleExternalLinkValue) => onSave(value),
  });

  const { field } = useController({ control, name: questionCard.id });

  const fieldValue = (field.value ?? []) as MultipleExternalLinkValue;

  const saveDocuments = (newValue: MultipleExternalLinkValue) =>
    saveMutation.mutateAsync(newValue, { onSuccess: () => field.onChange(newValue) });

  const updateDocument = (index: number, value: DocumentReference) =>
    saveDocuments(fieldValue.map((reference, i) => (i === index ? value : reference)));

  const addDocument = (value: DocumentReference) => saveDocuments([...fieldValue, value]);

  const removeLink = (index: number) => {
    const newValue = fieldValue.filter((_, i) => i !== index);
    field.onChange(newValue);
    return saveMutation.mutateAsync(newValue);
  };

  const handleRemoveClick = (reference: DocumentReference, index: number) => {
    if (reference.kind === "file") {
      setDialogState({ type: "confirmFileDelete", file: reference });
      return;
    }
    removeLink(index);
  };

  return (
    <>
      <Stack sx={{ width: "100%", my: fieldValue.length > 0 ? 0.5 : 0 }}>
        {fieldValue.map((reference, index) => {
          const isUrlLinkDisabled = readonly && reference.kind !== "file";
          return (
            <DocumentItem
              key={getDocumentReferenceKey(reference)}
              isReadOnly={readonly}
              reference={reference}
              onRemoveClick={() => handleRemoveClick(reference, index)}
              onEditClick={() => setDialogState({ type: "edit", reference, index })}
              linkSx={{ pointerEvents: isUrlLinkDisabled ? "none" : "auto" }}
            />
          );
        })}
        <Box>
          <AddDialogTrigger
            isMultistepFormVersion={false}
            isEmpty={fieldValue.length === 0}
            isReadOnly={readonly}
            renderDialog={props => (
              <DocumentDialog {...props} fileOwner={fileOwner} questionId={questionCard.id} onSubmit={addDocument} />
            )}
          >
            {t("Document.AddButton")}
          </AddDialogTrigger>

          {saveMutation.isError && <ErrorMessage />}
          {mandatoryQuestionErrorMessage && questionCard.mandatory && isAnswerRequestEmpty(field.value) && (
            <ErrorMessage variant="warning" errorMessage={mandatoryQuestionErrorMessage} />
          )}
        </Box>
      </Stack>

      {dialogState.type === "edit" && (
        <DocumentDialog
          fileOwner={fileOwner}
          questionId={questionCard.id}
          onClose={() => setDialogState({ type: "closed" })}
          onSubmit={reference => updateDocument(dialogState.index, reference)}
          defaultReference={dialogState.reference}
        />
      )}

      {dialogState.type === "confirmFileDelete" && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setDialogState({ type: "closed" })}
          onDelete={() =>
            saveDocuments(
              fieldValue.filter(reference => reference.kind !== "file" || reference.fileId !== dialogState.file.fileId)
            )
          }
          headerText={t("Document.Delete.ConfirmHeader")}
          message={t("Document.Delete.ConfirmMessage", { fileName: dialogState.file.fileName })}
          confirmButtonLabel={t("Document.Delete.ConfirmButton")}
        />
      )}
    </>
  );
};

type DocumentFormValues = DocumentSourceFormValues & { label: string };

const toFormValues = (reference?: DocumentReference): DocumentFormValues => ({
  label: reference?.label ?? "",
  ...toDocumentSourceFormValues(reference ?? {}),
});

const toDocumentReference = (
  { label, kind, url, file }: DocumentFormValues,
  savedFile?: FileReference
): DocumentReference => {
  const trimmedLabel = label.trim();
  if (kind === "link") return { label: trimmedLabel || url.trim(), url: url.trim() };
  if (!file) throw new Error("A file must be uploaded before the document is saved");

  const isLabelUnchangedFromSavedFile = savedFile !== undefined && trimmedLabel === savedFile.fileName;
  const shouldUseFileName = !trimmedLabel || isLabelUnchangedFromSavedFile;
  const { fileId, fileName, size } = file;
  const resolvedLabel = shouldUseFileName ? fileName : trimmedLabel;
  return { kind: "file", label: resolvedLabel, fileId, fileName, size };
};

type DocumentDialogProps = {
  onClose: () => void;
  onSubmit: (reference: DocumentReference) => Promise<unknown>;
  defaultReference?: DocumentReference;
  fileOwner: FileOwner | undefined;
  questionId: string;
};

const DocumentDialog: FC<DocumentDialogProps> = ({ onClose, onSubmit, defaultReference, fileOwner, questionId }) => {
  const { t } = useTranslation();
  const { handleSubmit, control, setFocus, setValue, getValues } = useForm<DocumentFormValues>({
    defaultValues: toFormValues(defaultReference),
  });
  const savedFile = defaultReference?.kind === "file" ? defaultReference : undefined;
  const submitMutation = useMutation({ mutationFn: onSubmit });

  FormDialog.useAutoFocusField(setFocus, "label");

  const handleKindChange = (value: DocumentFormValues["kind"]) => {
    if (value === "link" && savedFile && getValues("label").trim() === savedFile.fileName) setValue("label", "");
    if (value === "file") {
      const label = getValues("label").trim();
      const isLabelSameAsUrl = label !== "" && label === getValues("url").trim();
      if (isLabelSameAsUrl) setValue("label", "");
    }
  };

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Title>
        {defaultReference ? t("Document.Dialog.EditTitle") : t("Document.Dialog.AddTitle")}
      </FormDialog.Title>

      <FormDialog.Form
        onSubmit={handleSubmit(values =>
          submitMutation.mutate(toDocumentReference(values, savedFile), { onSuccess: onClose })
        )}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={submitMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="label"
              label={
                <>
                  {t("ExternalMultipleLink.Label")}{" "}
                  <Box component="span" sx={{ fontWeight: 400 }}>
                    {t("Document.Dialog.Optional")}
                  </Box>
                </>
              }
            />

            <DocumentSourceFields
              control={control}
              name="kind"
              onKindChange={handleKindChange}
              savedFileName={savedFile?.fileName}
              linkField={
                <FormDialog.TextField
                  control={control}
                  name="url"
                  label={t("ExternalMultipleLink.Url")}
                  rules={{ validate: url => url.trim() !== "" || t("Input.Validation.Required") }}
                  placeholder={t("ExternalMultipleLink.Placeholder")}
                  inputMode="url"
                  autoComplete="url"
                />
              }
              fileField={
                fileOwner && <FileField control={control} name="file" fileOwner={fileOwner} questionId={questionId} />
              }
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={submitMutation.isPending}>
            {defaultReference ? t("ExternalMultipleLink.UpdateButton") : t("ExternalMultipleLink.AddButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
